import { t } from 'i18next';
import { Button, ButtonToggle } from '@peacecover/component';
import { Xmark } from '@peacecover/icon';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import { useSubscriptionCancel } from './hooks/useSubscriptionCancel';
import { REFUND_REQUEST_REASONS } from '../../domains/payments/constants';
import type { RefundRequestReasonType } from './hooks/useBettieRefundRequest';

const SubscriptionCancelPage = () => {
  const handleHistoryBack = useHandleHistoryBack();
  const {
    handleCancelSubscription,
    selectedReasonType,
    setSelectedReasonType,
    cancelDetail,
    setCancelDetail
  } = useSubscriptionCancel();

  return (
    <div className="min-h-screen pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      <div className="px-[16px] py-[10px]">
        <button type="button" onClick={handleHistoryBack}>
          <Xmark size={24} className="text-text-main-lv1" />
        </button>
      </div>
      <div className="px-[24px]">
        <div className="mt-[14px] py-[10px] flex flex-col gap-[14px] items-start">
          <h1 className="text-[26px] font-bold text-text-main-lv1 leading-[31px]">
            {t('SubscriptionCancel.title')}
          </h1>
          <p className="text-[15px] font-medium text-text-main-lv4 leading-[18px]">
            {t('SubscriptionCancel.description')}
          </p>
        </div>
        <div className="mt-[28px] px-[24px] pb-[22px] bg-background-elevated rounded-[20px] shadow-[0_0_1px_0_rgba(0,0,0,0.05),0_0_10px_0_rgba(0,0,0,0.08)]">
          <div className="pt-[24px] pb-[18px] flex flex-col gap-[8px] items-start">
            <p className="text-[17px] font-bold text-text-main-lv2 leading-[20px]">
              {t('SubscriptionCancel.reasonTitle')}
            </p>
            <p className="text-[13px] text-text-main-lv3 leading-[16px]">
              {t('SubscriptionCancel.reasonSubscription')}
            </p>
          </div>
          <div>
            {REFUND_REQUEST_REASONS.map((item) => (
              <RefundRequestReasonItem
                key={item.id}
                reason={item}
                selectedReasonType={selectedReasonType}
                setSelectedReasonType={setSelectedReasonType}
              />
            ))}
            <textarea
              className="w-full p-[20px] bg-secondary-light dark:bg-secondary-dark rounded-[12px] text-[17px] font-medium leading-[20px] text-text-main-lv2 focus:outline-none"
              value={cancelDetail}
              onChange={(e) => setCancelDetail(e.target.value)}
              onFocus={() => setSelectedReasonType('others')}
              placeholder={t('SubscriptionCancel.placeholder') as string}
            />
          </div>
        </div>
        <div className="flex flex-col gap-[6px] items-start mt-[41px]">
          <p className="text-[13px] font-bold text-text-main-lv4 leading-[16px]">
            {t('SubscriptionCancel.checkTitle')}
          </p>
          <p className="text-[13px] font-medium text-text-main-lv4 leading-[16px] whitespace-pre-wrap">
            {t('SubscriptionCancel.checkDescription')}
          </p>
        </div>
      </div>
      <div className="w-full px-[24px] fixed bottom-0 pb-[calc(env(safe-area-inset-bottom)+10px)] bg-background-elevated">
        <Button
          size="large"
          className="w-full text-[17px] font-semibold leading-[20px]"
          onClick={handleCancelSubscription}
        >
          {t('SubscriptionCancel.button')}
        </Button>
      </div>
    </div>
  );
};

export default SubscriptionCancelPage;

const RefundRequestReasonItem = ({
  reason,
  selectedReasonType,
  setSelectedReasonType
}: {
  reason: (typeof REFUND_REQUEST_REASONS)[number];
  selectedReasonType: RefundRequestReasonType;
  setSelectedReasonType: (reasonType: RefundRequestReasonType) => void;
}) => {
  return (
    <label className="py-[10px] flex items-center gap-[17px]" htmlFor={reason.id.toString()}>
      <ButtonToggle
        id={reason.id.toString()}
        type="check"
        size="lg"
        value={reason.type}
        checked={selectedReasonType === reason.type}
        onChange={() => setSelectedReasonType(reason.type as RefundRequestReasonType)}
      />
      <p className="text-[15px] font-medium leading-[18px] text-text-main-lv2">{reason.title}</p>
    </label>
  );
};

